"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { Button, FormInput } from "@offstreetparking/design-system/forms";
import { Link, Text } from "@offstreetparking/design-system/typography";
import { isEmailInDomains, zodEmailValidation } from "@offstreetparking/domain";
import { Form } from "@offstreetparking/portal-flow/components/forms/Form";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import { useId } from "react";
import * as z from "zod";
import { getCompanySsoLoginUrl } from "~/app/[locale]/login/actions";
import { OrDivider } from "~/components/auth/OrDivider";
import { ProviderLoginButtons } from "~/components/auth/ProviderLoginButtons";
import { useLoginPopup } from "~/components/auth/useLoginPopup";
import { useCompany } from "~/components/providers/CompanyProvider";
import type { AuthError } from "~/lib/auth";
import { AUTH_ERROR_MESSAGES } from "~/lib/auth/i18n-error";
import { routes } from "~/routes";
const createLoginSchema = ({
	ssoDomains,
	invalidSsoEmailMessage,
	userMustUseSsoMessage,
}: {
	ssoDomains?: string[];
	invalidSsoEmailMessage: string;
	userMustUseSsoMessage: string;
}) =>
	z.discriminatedUnion("mode", [
		z.object({
			mode: z.literal("sso"),
			email: zodEmailValidation.refine((email) => !ssoDomains || isEmailInDomains(email, ssoDomains), {
				message: invalidSsoEmailMessage,
			}),
			companyId: z.number().optional(),
			subdomain: z.string().optional(),
		}),
		z.object({
			mode: z.literal("password"),
			email: zodEmailValidation.refine((email) => !ssoDomains || !isEmailInDomains(email, ssoDomains), {
				message: userMustUseSsoMessage,
			}),
			password: z.string().min(8),
			companyId: z.number().optional(),
			subdomain: z.string().optional(),
		}),
	]);
type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;
export type LoginFormProps = {
	onRedirectToSSO: (
		options: Extract<
			LoginFormData,
			{
				mode: "sso";
			}
		>,
	) => Promise<void>;
	onLoginWithPassword: (
		options: Extract<
			LoginFormData,
			{
				mode: "password";
			}
		>,
	) => Promise<void>;
	redirectTo?: string;
	email?: string;
	onSwitchTo?: (target: "signUp" | "forgotPassword") => void;
	onProviderSignedIn?: () => void;
};
export const LoginFormContent = ({
	onRedirectToSSO,
	onLoginWithPassword,
	redirectTo,
	email,
	onSwitchTo,
	onProviderSignedIn,
}: LoginFormProps) => {
	const { t, i18n } = useLingui();
	const searchParams = useSearchParams();
	const formId = useId();
	const { company } = useCompany();
	const { run: runSsoPopup } = useLoginPopup({ onSignedIn: onProviderSignedIn });
	const errorParam = searchParams.get("error");
	const {
		mutate: login,
		isPending,
		error,
		variables: lastSubmitted,
	} = useMutation<{ success: true }, AuthError, LoginFormData>({
		mutationFn: async (data) => {
			if (data.mode === "sso") {
				const { companyId, email: ssoEmail, subdomain } = data;
				await runSsoPopup({
					getPopupUrl: (popupRedirectTo) =>
						getCompanySsoLoginUrl({ companyId, email: ssoEmail, subdomain, redirectTo: popupRedirectTo }),
					redirectPage: () => onRedirectToSSO(data),
				});
			} else {
				await onLoginWithPassword(data);
			}
			return {
				success: true,
			};
		},
	});

	const isNextRedirectError = error?.message !== "NEXT_REDIRECT";
	const hasError = Boolean(error) && isNextRedirectError;
	const ssoEnabled = Boolean(company?.ssoEnabled);
	const defaultToSso = email
		? ssoEnabled && (!company?.ssoDomains || isEmailInDomains(email, company.ssoDomains))
		: ssoEnabled;
	const emailDomain = lastSubmitted?.email?.split("@")[1];
	const getErrorMessage = () => {
		if (error?.i18nKey === "auth:login.errors.external-organization" && emailDomain) {
			return (
				<Trans>
					Your <strong>{emailDomain}</strong> account can't be used with other organizations. Create an
					account with another email or contact your administrator.
				</Trans>
			);
		}
		if (error?.i18nKey) {
			return i18n._(AUTH_ERROR_MESSAGES[error.i18nKey]);
		}
		return error?.message;
	};
	const errorMessage = getErrorMessage();

	return (
		<>
			{(errorParam || hasError) && (
				<div className="rounded-md bg-critical-faded p-4">
					<Text size="sm" className="text-critical">
						{errorParam === "missing_code" && t`Authentication code is missing. Please try again.`}
						{errorParam === "authentication_failed" && t`Authentication failed. Please try again.`}
						{errorMessage}
					</Text>
				</div>
			)}

			<Form
				id={formId}
				className="flex flex-col items-stretch space-y-6"
				schema={createLoginSchema({
					ssoDomains: company?.ssoDomains,
					invalidSsoEmailMessage: t`Please enter an email associated with your organization to use Single Sign-On.`,
					userMustUseSsoMessage: t`Please use Single Sign-On to sign in to your account.`,
				})}
				initialValues={{
					mode: defaultToSso ? "sso" : "password",
					email: email ?? "",
					password: "",
					companyId: company?.id,
					subdomain: company?.subdomain,
				}}
				onSubmit={(values: LoginFormData) => login(values)}
			>
				{({ watch, setValue, clearErrors }) => (
					<>
						{watch("mode") === "sso" && (
							<>
								<FormInput
									id="email"
									name="email"
									type="email"
									label={t`Email`}
									autoComplete="email"
									isRequired
								/>

								<Button isLoading={isPending} type="submit" form={formId} className="w-full">
									{t`Continue with Single Sign-On`}
								</Button>

								<OrDivider />

								<Button
									intent="secondary"
									type="button"
									onClick={() => {
										setValue("mode", "password");
										clearErrors("email");
									}}
									className="w-full"
								>
									{t`Sign in with password instead`}
								</Button>

								<SignUpPrompt
									redirectTo={redirectTo}
									onSwitchToSignUp={onSwitchTo && (() => onSwitchTo("signUp"))}
								/>
							</>
						)}
						{watch("mode") === "password" && (
							<>
								<FormInput name="email" type="email" label={t`Email`} autoComplete="email" isRequired />

								<FormInput
									name="password"
									type="password"
									label={t`Password`}
									autoComplete="current-password"
									isRequired
								/>

								<div className="-mt-2 text-right">
									{onSwitchTo ? (
										<Button
											intent="action"
											type="button"
											className="text-sm"
											onClick={() => onSwitchTo("forgotPassword")}
										>
											{t`Forgot your password?`}
										</Button>
									) : (
										<Link
											href={routes.forgotPassword({
												redirectTo,
											})}
											className="text-sm"
										>
											{t`Forgot your password?`}
										</Link>
									)}
								</div>

								<Button isLoading={isPending} type="submit" form={formId} className="w-full">
									{t`Sign In`}
								</Button>

								<OrDivider />

								<ProviderLoginButtons redirectTo={redirectTo} onSignedIn={onProviderSignedIn} />
								{company?.ssoEnabled && (
									<Button
										intent="secondary"
										type="button"
										onClick={() => {
											setValue("mode", "sso");
											clearErrors("email");
										}}
										className="w-full"
									>
										{t`Sign in with Single Sign-On instead`}
									</Button>
								)}
								<SignUpPrompt
									redirectTo={redirectTo}
									onSwitchToSignUp={onSwitchTo && (() => onSwitchTo("signUp"))}
								/>
							</>
						)}
					</>
				)}
			</Form>
		</>
	);
};

const SignUpPrompt = ({ redirectTo, onSwitchToSignUp }: { redirectTo?: string; onSwitchToSignUp?: () => void }) => {
	const { t } = useLingui();
	return (
		<div className="space-y-2 pt-4 text-center">
			<Text weight="medium">{t`Don't have an account?`}</Text>
			{onSwitchToSignUp ? (
				<Button intent="secondary" type="button" onClick={onSwitchToSignUp} className="w-full">
					{t`Sign up`}
				</Button>
			) : (
				<Link href={routes.signup({ redirectTo })} className="block">
					{t`Sign up`}
				</Link>
			)}
		</div>
	);
};
